import { DateFormat, DateService } from 'frontend-toolkit';
import { InstallmentPlan } from '../types';
import { PaymentPlanFormValues, PlanType } from './types';

// convert the plan data structure into easier values for the UI
export const getFormValuesFromPlan = (
  plan: Partial<InstallmentPlan>
): PaymentPlanFormValues => {
  // values that need tweaks to be more UI friendly

  // planType is either Custom or Dynamic in UI but is stored as an option in frequencyType
  const planType =
    plan.frequencyType === 'custom' ? PlanType.Custom : PlanType.Dynamic;

  // UI uses different values for withdrawDay based on the frequencyType
  const withdrawDayWeekly = plan.withdrawBasedOnCheckout
    ? 'withdrawBasedOnCheckout'
    : plan.frequencyType === 'weekly'
    ? plan.withdrawDay
    : 'monday';
  const withdrawDayMonthly = plan.withdrawBasedOnCheckout
    ? 'withdrawBasedOnCheckout'
    : plan.frequencyType === 'monthly'
    ? plan.withdrawDay
    : '1';

  // convert the frequency type to biweekly if frequencyAmount is 2
  const frequencyType =
    plan.frequencyType === 'weekly' && plan.frequencyAmount === 2
      ? 'biweekly'
      : plan.frequencyType === 'custom'
      ? 'monthly' // default unused to monthly if it's a custom schedule
      : plan.frequencyType;

  // convert the deposit amount to dollars if it's an amount
  const depositAmount = (plan.depositAmount || 0) / 100;
  // set the scheduled dates to an empty array if it's not set
  const scheduledDates = plan.scheduledDates || [];
  return {
    id: plan.id,
    name: plan.name,
    depositType: plan.depositType,
    installmentCount: plan.installmentCount,
    applyDepositPerRegistration: plan.applyDepositPerRegistration,
    planType,
    frequencyType,
    withdrawDayWeekly,
    withdrawDayMonthly,
    depositAmount,
    scheduledDates,
  };
};

// convert the form structure into plan data structure
export const getPlanFromFormValues = (
  formValues: PaymentPlanFormValues
): Partial<InstallmentPlan> => {
  // convert the deposit amount to cents if it's an amount
  // the percentage is also converted to an integer
  const depositAmount =
    formValues.depositType === 'first_installment'
      ? null
      : Number((formValues.depositAmount * 100).toFixed());

  // convert the biweekly frequency type to separate fields
  let frequencyType =
    formValues.frequencyType === 'biweekly'
      ? 'weekly'
      : formValues.frequencyType;
  let frequencyAmount = formValues.frequencyType === 'biweekly' ? 2 : 1;

  let scheduledDates;
  if (formValues.planType === 'custom') {
    frequencyAmount = 0; // set to 0 for custom schedule
    frequencyType = 'custom'; // overwrite the frequency type if it's a custom schedule plan
    scheduledDates = formValues.scheduledDates;
  }

  const withdrawDayCombined =
    formValues.frequencyType === 'monthly'
      ? formValues.withdrawDayMonthly
      : formValues.withdrawDayWeekly;

  const withdrawBasedOnCheckout =
    withdrawDayCombined === 'withdrawBasedOnCheckout';
  const withdrawDay = withdrawBasedOnCheckout ? '' : withdrawDayCombined;

  // not used in BE for custom but temporarily needs a value to pass save validation
  const installmentCount =
    formValues.planType === 'custom' ? 0 : formValues.installmentCount;

  return {
    depositAmount,
    frequencyType,
    frequencyAmount,
    scheduledDates,
    withdrawDay,
    withdrawBasedOnCheckout,
    name: formValues.name,
    depositType: formValues.depositType,
    installmentCount,
    applyDepositPerRegistration: formValues.applyDepositPerRegistration,
  };
};

export const validateScheduledDates = (scheduledDates: string[], originalValue?: string[]): string[] => {
  if (scheduledDates.length === 0) {
    return ['Must set at least one scheduled installment'];
  }
  const errors: string[] = scheduledDates.map((date, i) => {
    const dateObj = new Date(date);
    const isValidDate = dateObj && !isNaN(dateObj.getTime());
    const valueMatchesOriginalValue = originalValue && originalValue[i] && originalValue[i] === date;
    if (!isValidDate) {
      return 'Invalid date format';
    }
    if (!valueMatchesOriginalValue && dateObj < new Date(datePresets.getOneYearAgo())) {
      return 'Date is out of range';
    }
    const nextDateObj = scheduledDates[i + 1]
      ? new Date(scheduledDates[i + 1])
      : null;

    const prevDateObj = scheduledDates[i - 1]
      ? new Date(scheduledDates[i - 1])
      : null;

    const isNextDateValid = nextDateObj && !isNaN(nextDateObj.getTime());
    const isPrevDateValid = prevDateObj && !isNaN(prevDateObj.getTime());
    if (isPrevDateValid && dateObj <= prevDateObj) {
      return 'Must be in ascending order';
    }
    if (isNextDateValid && dateObj >= nextDateObj) {
      return 'Must be in ascending order';
    }
    return undefined; // explicit falsy value for no error
  });
  return errors;
};

export const validateFormField = (
  formValues: PaymentPlanFormValues,
  errors: Record<string, string[]>,
  field: string,
  value?: string | number,
  originalformValues?: PaymentPlanFormValues, // to compare unchanged values
): Record<string, string[]> => {
  if (field === 'name' && !value) {
    return { ...errors, name: ['Name is required'] };
  }
  if (field === 'depositType' && value === 'first_installment') {
    return { ...errors, depositAmount: [] }; // reset error if deposit type switched to first installment
  }
  if (field === 'depositAmount' && !value) {
    return { ...errors, depositAmount: ['Amount is required'] };
  }
  if (
    field === 'depositAmount' &&
    formValues.depositType === 'amount' &&
    Number(value) < 6
  ) {
    return { ...errors, depositAmount: ['Minimum amount is 6.00'] };
  }
  if (
    field === 'depositAmount' &&
    formValues.depositType === 'percentage' &&
    Number(value) > 100
  ) {
    return { ...errors, depositAmount: ['Maximum amount is 100'] };
  }
  if (
    field === 'depositAmount' &&
    formValues.depositType === 'percentage' &&
    Number(value) < 1
  ) {
    return { ...errors, depositAmount: ['Minimum amount is 1'] };
  }
  if (field === 'scheduledDates') {
    return {
      ...errors,
      scheduledDates: validateScheduledDates(value as unknown as string[], originalformValues?.scheduledDates),
    };
  }
  // if no conditions are met, clear the field error
  return { ...errors, [field]: [] };
};

export const requiredFieldsMissing = (
  formValues: PaymentPlanFormValues
): boolean => {
  const depositFields =
    formValues.depositType === 'amount' ? ['depositAmount'] : [];
  const requiredFields =
    formValues.planType === 'dynamic'
      ? ['name', 'installmentCount', ...depositFields]
      : ['name', ...depositFields];
  return requiredFields.some((field) => !formValues[field]);
};

export const datePresets = {
  // in object form for easy mocking across test types
  getToday: () => DateService.format(new Date(), DateFormat.YYYY_MM_DD),
  getOneMonthFromNow: () => {
    const datePlusOneMonth = new Date();
    datePlusOneMonth.setMonth(datePlusOneMonth.getMonth() + 1);
    return DateService.format(datePlusOneMonth, DateFormat.YYYY_MM_DD);
  },
  getOneMonthFromDate: (date: string) => {
    const datePlusOneMonth = new Date(date);
    datePlusOneMonth.setMonth(datePlusOneMonth.getMonth() + 1);
    return DateService.format(datePlusOneMonth, DateFormat.YYYY_MM_DD);
  },
  getOneYearAgo: () => {
    const dateMinusOneYear = new Date();
    dateMinusOneYear.setFullYear(dateMinusOneYear.getFullYear() - 1);
    return DateService.format(dateMinusOneYear, DateFormat.YYYY_MM_DD);
  }
};
